import { Tooltip, Typography, Chip, Stack } from '@mui/material';

import { Column } from 'common/components/Table/CommonTableStyles';
import {
	SORT_DIRECTION_ASC,
	CARD_SIZE_12,
	TABLE_SIZE_10,
} from 'common/constants';
import { rrcpDashboardRoutes } from 'common/constants/rrcp-dashboard-routes';
import {
	BUSINESS_UNIT_DETAIL,
	DEFAULT_CONTROL_TABLE_COLUMN,
	DEFAULT_OBLIGATION_TABLE_COLUMN,
	DEFAULT_POLICY_TABLE_COLUMN,
	DEFAULT_PROCESS_TABLE_COLUMN,
	DEFAULT_RISK_TABLE_COLUMN,
	ENTITY_TYPE,
	TAXONOMY_TAB_VALUES,
	LAST_UPDATED_LABEL,
	OWNER_LABEL,
} from 'common/constants/TaxonomyConstants';
import {
	dateInCorrectFormatWithFullYear,
	downloadDocAndSave,
	toSentenceCase,
} from 'common/utils/utilFunctions';

import { ITableQuery } from 'hooks/useTableQuery';

import { IRiskTableProps } from 'features/RiskProfile/api/type-def';

import {
	ICitationList,
	IControlTableData,
	IObligationTableData,
	IPoliciesTableData,
	IProcessesTableData,
	RiskRefCardData,
} from '../api/type-def';

import styles from './RisksTaxonomyTable/RisksTaxonomyTable.module.scss';

const {
	CITATIONS_VALUE,
	PROCESSES_VALUE,
	CONTROLS_VALUE,
	RISKS_VALUE,
	REGULATIONS_VALUE,
	OBLIGATIONS_VALUE,
	POLICIES_VALUE,
} = TAXONOMY_TAB_VALUES;
const { NAME, USER_NAME } = BUSINESS_UNIT_DETAIL;

const {
	parent,
	prcInventory,
	regulationInventory,
	overView,
	regulationMappingAssessment,
} = rrcpDashboardRoutes;

export const countTabTaxonomyMapping = (
	label: string,
	data: any,
	result: any,
) => {
	const tempObj = result;
	switch (label) {
		case ENTITY_TYPE.CITATION:
			tempObj.citationCount = data.count;
			return tempObj;
		case ENTITY_TYPE.PROCESS:
			tempObj.processCount = data.count;
			return tempObj;
		case ENTITY_TYPE.RISK:
			tempObj.riskCount = data.count;
			return tempObj;
		case ENTITY_TYPE.CONTROL:
			tempObj.controlCount = data.count;
			return tempObj;
		case ENTITY_TYPE.OBLIGATION:
			tempObj.obligationCount = data.count;
			return tempObj;
		case ENTITY_TYPE.POLICY:
			tempObj.policyCount = data.count;
			return tempObj;
		default:
			return tempObj;
	}
};

export const getSortKey = (tableType: TAXONOMY_TAB_VALUES) => {
	let sortKey;
	switch (tableType) {
		case OBLIGATIONS_VALUE:
			sortKey = DEFAULT_OBLIGATION_TABLE_COLUMN;
			break;
		case PROCESSES_VALUE:
			sortKey = DEFAULT_PROCESS_TABLE_COLUMN;
			break;
		case RISKS_VALUE:
			sortKey = DEFAULT_RISK_TABLE_COLUMN;
			break;
		case CONTROLS_VALUE:
			sortKey = DEFAULT_CONTROL_TABLE_COLUMN;
			break;
		case POLICIES_VALUE:
			sortKey = DEFAULT_POLICY_TABLE_COLUMN;
			break;
		default:
			break;
	}
	return sortKey;
};

export const getDefaultRiskTableQueryParams = (
	tableType: TAXONOMY_TAB_VALUES,
	viewType: string,
) => {
	const pageParams = {
		pageNumber: 0,
		pageSize: viewType === 'CARD' ? CARD_SIZE_12 : TABLE_SIZE_10,
	};
	const sortKeyVal = getSortKey(tableType);
	const sortParams = sortKeyVal
		? { sortKey: sortKeyVal, sortDirection: SORT_DIRECTION_ASC }
		: {};

	return { ...pageParams, ...sortParams };
};

export const getApiData = (data: any, tableType: TAXONOMY_TAB_VALUES) => {
	const sortKeyVal = getSortKey(tableType);
	const sortKey = data?.sortKey ? data?.sortKey : sortKeyVal;
	const sortDirection = data?.sortDirection
		? data?.sortDirection
		: SORT_DIRECTION_ASC;
	const sortParams = sortKeyVal ? { sortKey, sortDirection } : {};
	return { ...data, ...sortParams };
};

export const getDefaultSortKey = (tableType: TAXONOMY_TAB_VALUES) => {
	switch (tableType) {
		case OBLIGATIONS_VALUE:
			return DEFAULT_OBLIGATION_TABLE_COLUMN;
		case PROCESSES_VALUE:
			return DEFAULT_PROCESS_TABLE_COLUMN;
		case RISKS_VALUE:
			return DEFAULT_RISK_TABLE_COLUMN;
		case CONTROLS_VALUE:
			return DEFAULT_CONTROL_TABLE_COLUMN;
		case POLICIES_VALUE:
			return DEFAULT_POLICY_TABLE_COLUMN;
		default:
			return '';
	}
};

export const getTableQuery = (
	obligationTableQuery: ITableQuery,
	citationTableQuery: ITableQuery,
	processTableQuery: ITableQuery,
	riskTableQuery: ITableQuery,
	controlTableQuery: ITableQuery,
	tableType: TAXONOMY_TAB_VALUES,
) => {
	switch (tableType) {
		case CITATIONS_VALUE:
			return citationTableQuery;
		case PROCESSES_VALUE:
			return processTableQuery;
		case RISKS_VALUE:
			return riskTableQuery;
		case CONTROLS_VALUE:
			return controlTableQuery;
		default:
			return obligationTableQuery;
	}
};

export const getNavigateLink = (
	params: any,
	category: TAXONOMY_TAB_VALUES,
	pathname: string,
) => {
	const { id, regulationId, citationId } = params;
	switch (category) {
		case OBLIGATIONS_VALUE:
			return `/${regulationInventory}/${overView}/${regulationMappingAssessment}/${id}`;
		case PROCESSES_VALUE:
			return pathname === parent
				? `${parent}/${id}/${ENTITY_TYPE.PROCESS}`
				: `/${prcInventory}/${id}/${ENTITY_TYPE.PROCESS}`;
		case RISKS_VALUE:
			return pathname === parent
				? `${parent}/${id}/${ENTITY_TYPE.RISK}`
				: `/${prcInventory}/${id}/${ENTITY_TYPE.RISK}`;
		case CONTROLS_VALUE:
			return pathname === parent
				? `${parent}/${id}/${ENTITY_TYPE.CONTROL}`
				: `/${prcInventory}/${id}/${ENTITY_TYPE.CONTROL}`;
		case REGULATIONS_VALUE:
			return pathname === parent
				? `${parent}/${regulationId}`
				: `/${prcInventory}/${regulationId}`;
		case CITATIONS_VALUE:
			return `/${prcInventory}/${citationId}`;
		default:
			return '';
	}
};

export const getRiskRefTaxonomyColumn = (
	tableType: TAXONOMY_TAB_VALUES,
	handleNavigate: (params: any, category: TAXONOMY_TAB_VALUES) => Promise<void>,
	riskRefTaxonomySourceFiltersKey: string,
	isRequirementInventoryModuleExists: boolean,
) => {
	const obligationTableColunms: Column[] = [
		{
			id: 'groupName',
			label: 'Obligation Name',
			disablePadding: false,
			isSortable: true,
			cellClassName: styles['text-styling-wrapper'],
			render: (params: IObligationTableData) =>
				isRequirementInventoryModuleExists ? (
					<Typography
						data-testid="official-citation-name-link"
						onClick={() => handleNavigate(params, OBLIGATIONS_VALUE)}
						className={styles['text-section-clickable']}
					>
						{params?.groupName}
					</Typography>
				) : (
					<Typography
						data-testid="official-citation-name-link"
						className={styles['text-section-unclickable']}
					>
						{params?.groupName}
					</Typography>
				),
		},
		{
			id: 'businessUnitDetailDTOS',
			label: 'Business units',
			disablePadding: false,
			cellClassName: styles['text-styling-wrapper-regulator'],
			render: (params: IObligationTableData) => (
				<Typography className={styles['text-section']}>
					<Tooltip
						title={getBusinessUnitsObjectStr(params, NAME)}
						placement="bottom-start"
					>
						<span>{getBusinessUnitsObjectStr(params, NAME)}</span>
					</Tooltip>
				</Typography>
			),
		},

		{ id: 'processCount', label: 'Processes', disablePadding: false },
		{ id: 'riskCount', label: 'Risks', disablePadding: false },
		{ id: 'controlCount', label: 'Controls', disablePadding: false },
		{ id: 'policyCount', label: 'Policies', disablePadding: false },
		{
			id: 'businessUnitDetailDTOS',
			label: 'Owner',
			disablePadding: false,
			cellClassName: styles['text-styling-wrapper-regulator'],
			render: (params: IObligationTableData) => (
				<Typography className={styles['text-section']}>
					<Tooltip
						title={getBusinessUnitsObjectStr(params, USER_NAME)}
						placement="bottom-start"
					>
						<span>{getBusinessUnitsObjectStr(params, USER_NAME)}</span>
					</Tooltip>
				</Typography>
			),
		},
		{
			id: 'modifiedDate',
			label: 'Last updated',
			disablePadding: false,
			isSortable: true,
			render: (params: ICitationList) => (
				<Typography className={styles['table-date']}>
					{dateInCorrectFormatWithFullYear(params?.modifiedDate)}
				</Typography>
			),
		},
	];
	const citationsTableColumns: Column[] = [
		{
			id: 'paragraphId',
			label: 'Citation',
			disablePadding: false,
			isSortable: true,
			cellClassName: styles['text-styling-wrapper'],
			render: (params: ICitationList) => (
				<Typography
					data-testid="official-citation-name-link"
					onClick={() => handleNavigate(params, CITATIONS_VALUE)}
					className={styles['text-section-clickable']}
				>
					<Tooltip title={params?.citationTitle} placement="bottom-start">
						<span>{params?.citationTitle}</span>
					</Tooltip>
				</Typography>
			),
		},
		{
			id: 'regOfficialId',
			label: 'Regulation',
			disablePadding: false,
			isSortable: true,
			cellClassName: styles['text-styling-wrapper'],
			render: (params: ICitationList) => (
				<Typography
					component="div"
					data-testid="regulation-link"
					onClick={() => handleNavigate(params, REGULATIONS_VALUE)}
					className={styles['text-section-clickable']}
				>
					<Tooltip title={params?.regOfficialId} placement="bottom-start">
						<span>{params?.regOfficialId}</span>
					</Tooltip>
				</Typography>
			),
		},
		{
			id: 'jurisdiction',
			label: 'Jurisdiction',
			disablePadding: false,
			isSortable: true,
			cellClassName: styles['text-styling-wrapper-regulator'],
			render: ({ jurisdiction }: ICitationList) => (
				<Typography className={styles['text-section-pointer']}>
					<Tooltip title={jurisdiction} placement="bottom-start">
						<span>{jurisdiction}</span>
					</Tooltip>
				</Typography>
			),
		},
		{ id: 'processCount', label: 'Processes', disablePadding: false },
		{ id: 'riskCount', label: 'Risks', disablePadding: false },
		{ id: 'controlCount', label: 'Controls', disablePadding: false },
		{
			id: 'modifiedDate',
			label: 'Last updated',
			disablePadding: false,
			isSortable: false,
			render: (params: ICitationList) => (
				<Typography className={styles['table-date']}>
					{dateInCorrectFormatWithFullYear(params?.modifiedDate)}
				</Typography>
			),
		},
	];

	const processesTableColumns: Column[] = [
		{
			id: 'processName',
			label: 'Process name',
			disablePadding: false,
			isSortable: true,
			cellClassName: styles['text-styling-wrapper-clickable-process'],
			render: (params: IProcessesTableData) => (
				<Typography
					component="div"
					data-testid="process-link"
					onClick={() => handleNavigate(params, PROCESSES_VALUE)}
					className={styles['text-section-clickable']}
				>
					<Tooltip title={params?.processName} placement="bottom-start">
						<span>{params?.processName}</span>
					</Tooltip>
				</Typography>
			),
		},

		{
			label: 'Risks',
			id: 'riskCount',
			isSortable: false,
			disablePadding: false,
		},
		{
			label: 'Controls',
			id: 'controlCount',
			disablePadding: false,
		},
		{
			id: 'industryType',
			label: 'Industry',
			disablePadding: false,
			isSortable: false,
		},
	];

	const risksTableColumns: Column[] = [
		{
			id: 'riskName',
			label: 'Risk category',
			disablePadding: false,
			cellClassName: styles['text-styling-wrapper-clickable'],
			isSortable: true,
			render: (params: IRiskTableProps) => (
				<Typography
					component="div"
					data-testid="riskid-link"
					onClick={() => handleNavigate(params, RISKS_VALUE)}
					className={styles['text-section-clickable']}
				>
					<Tooltip title={params?.riskName} placement="bottom-start">
						<span>{params?.riskName}</span>
					</Tooltip>
				</Typography>
			),
		},
		{
			id: 'riskStatement',
			label: 'Risk Statement',
			disablePadding: false,
			cellClassName: styles['text-styling-wrapper'],
			isSortable: true,
			render: ({ riskStatement }: any) => (
				<Typography className={styles['text-section']}>
					<Tooltip title={riskStatement} placement="bottom-start">
						<span>{riskStatement}</span>
					</Tooltip>
				</Typography>
			),
		},

		{
			id: 'processCount',
			disablePadding: false,
			isSortable: false,
			label: 'Processes',
		},
		{
			id: 'controlCount',
			disablePadding: false,
			label: 'Controls',
			isSortable: false,
		},
		{
			id: 'industryType',
			label: 'Industry',
			disablePadding: false,
			isSortable: false,
		},
	];

	const controlsTableColumns: Column[] = [
		{
			id: 'controlName',
			label: 'Control name',
			isSortable: true,
			disablePadding: false,
			cellClassName: styles['text-styling-wrapper-clickable'],
			render: (params: IControlTableData) => (
				<Typography
					component="div"
					data-testid="riskname-link"
					onClick={() => handleNavigate(params, CONTROLS_VALUE)}
					className={styles['text-section-clickable']}
				>
					<Tooltip title={params?.controlName} placement="bottom-start">
						<span>{params?.controlName}</span>
					</Tooltip>
				</Typography>
			),
		},
		{
			id: 'frequency',
			label: 'Frequency',
			disablePadding: false,
			isSortable: false,
		},
		{
			id: 'keyControl',
			label: 'Key/Non-key',
			disablePadding: false,
			isSortable: false,
		},
		{
			id: 'controlType',
			label: 'Type',
			disablePadding: false,
			isSortable: true,
		},
		{
			id: 'automatedOrManual',
			label: 'Automated/manual',
			disablePadding: false,
			isSortable: false,
		},
		{
			id: 'industryType',
			label: 'Industry',
			disablePadding: false,
			isSortable: false,
		},
	];

	const policiesTableColumns: Column[] = [
		{
			id: 'policyName',
			label: 'Policy name',
			isSortable: true,
			disablePadding: false,
			cellClassName: styles['text-styling-wrapper-clickable'],
			render: (params: IPoliciesTableData) => (
				<Typography
					component="div"
					data-testid="policyname-link"
					className={styles['text-section-clickable']}
					onClick={() => {
						downloadDocAndSave(params?.fileName);
					}}
				>
					<Tooltip title={params?.policyName} placement="bottom-start">
						<span>{params?.policyName}</span>
					</Tooltip>
				</Typography>
			),
		},
		{
			id: 'policyPurpose',
			label: 'Purpose',
			disablePadding: false,
			isSortable: false,
			cellClassName: styles['text-styling-wrapper-clickable-process'],
			render: (params: IPoliciesTableData) => (
				<Typography className={styles['text-section']}>
					<Tooltip title={params?.policyPurpose} placement="bottom-start">
						<span>{params?.policyPurpose}</span>
					</Tooltip>
				</Typography>
			),
		},
		{
			id: 'policyType',
			label: 'Type',
			disablePadding: false,
			isSortable: true,
			cellClassName: styles['text-styling-wrapper-clickable-process'],
			render: (params: IPoliciesTableData) => (
				<Typography className={styles['text-section']}>
					<Tooltip title={params?.policyType} placement="bottom-start">
						<span>{params?.policyType}</span>
					</Tooltip>
				</Typography>
			),
		},
		{
			id: 'version',
			label: 'Revision number',
			disablePadding: false,
			isSortable: false,
			isAlignRight: true,
			cellClassName: styles['text-styling-wrapper-align-right'],
		},
		{
			id: 'effectiveFrom',
			label: 'Effective from',
			disablePadding: false,
			isSortable: true,
			isAlignRight: true,
			cellClassName: styles['text-styling-wrapper-align-right'],
			render: (params: IPoliciesTableData) => (
				<Typography className={styles['table-date']}>
					{dateInCorrectFormatWithFullYear(params?.effectiveFrom)}
				</Typography>
			),
		},
		{
			id: 'effectiveTo',
			label: 'Effective to',
			disablePadding: false,
			isSortable: true,
			isAlignRight: true,
			cellClassName: styles['text-styling-wrapper-align-right'],
			render: (params: IPoliciesTableData) => (
				<Typography className={styles['table-date']}>
					{dateInCorrectFormatWithFullYear(params?.effectiveTo)}
				</Typography>
			),
		},
		{
			id: 'policyOwner',
			label: 'Owner',
			disablePadding: false,
			isSortable: true,
		},
	];

	switch (tableType) {
		case OBLIGATIONS_VALUE:
			return obligationTableColunms;
		case PROCESSES_VALUE:
			return processesTableColumns;
		case RISKS_VALUE:
			return risksTableColumns;
		case CONTROLS_VALUE:
			return controlsTableColumns;
		case POLICIES_VALUE:
			return policiesTableColumns;
		default:
			return citationsTableColumns;
	}
};

const getBusinessUnitsObjectStr = (
	param: IObligationTableData,
	key: BUSINESS_UNIT_DETAIL,
): string => {
	const businessUnitArr =
		param?.businessUnitDetailDTOS?.map((res: any) => {
			const element = JSON.parse(res);
			return element[key] || '-';
		}) || [];
	const uniqueArr = Array.from(new Set(businessUnitArr));
	return uniqueArr.join(',');
};
export const getBusinessUnitsCount = (
	param: IObligationTableData,
	key: BUSINESS_UNIT_DETAIL,
): number => {
	const businessUnitArr =
		param?.businessUnitDetailDTOS?.map((res: any) => {
			const element = JSON.parse(res);

			return element[key] || '-';
		}) || [];

	const uniqueArr = Array.from(new Set(businessUnitArr));

	return uniqueArr.length;
};
export const getChipLabel = (
	label: string,
	count: number,
	showCount: boolean,
) => {
	const displayLabel = showCount ? `${label}: ${count || 0}` : label;
	return displayLabel;
};

export const getChipInfo = (
	className: string,
	label: string,
	iconClass: React.ReactElement,
) => (
	<Chip
		className={className}
		label={label}
		icon={iconClass}
		variant="outlined"
		key={label}
	/>
);

export const getPrcCardEntityId = (
	data: RiskRefCardData,
	category: TAXONOMY_TAB_VALUES,
) => {
	switch (category) {
		case PROCESSES_VALUE:
		case CONTROLS_VALUE:
			return (data as IProcessesTableData | IControlTableData).displayId;
		case RISKS_VALUE:
			return (data as IRiskTableProps).displayId;
		case OBLIGATIONS_VALUE:
		case POLICIES_VALUE:
			return '';
		default:
			return (data as IProcessesTableData).processName;
	}
};

export const getPrcCardTitle = (
	data: RiskRefCardData,
	category: TAXONOMY_TAB_VALUES,
) => {
	switch (category) {
		case PROCESSES_VALUE:
			return (data as IProcessesTableData).processName || '';
		case CONTROLS_VALUE:
			return (data as IControlTableData).controlName || '';
		case RISKS_VALUE:
			return (data as IRiskTableProps).riskName || '';
		case OBLIGATIONS_VALUE:
			return (data as IObligationTableData).groupName || '';
		case POLICIES_VALUE:
			return (data as IPoliciesTableData).policyName || '';
		default:
			return '';
	}
};

export const getPrcCardTitleTwo = (
	data: RiskRefCardData,
	category: TAXONOMY_TAB_VALUES,
) => {
	switch (category) {
		case RISKS_VALUE:
			return (data as IRiskTableProps)?.riskStatement;
		case POLICIES_VALUE:
			return (data as IPoliciesTableData)?.policyPurpose;
		case CONTROLS_VALUE:
			return '';
		default:
			return (data as IRiskTableProps)?.riskStatement;
	}
};
export const getChipFormattedData = (
	data: RiskRefCardData,
	category: TAXONOMY_TAB_VALUES,
	className: string,
) => {
	switch (category) {
		case PROCESSES_VALUE: {
			const { riskCount, controlCount, industryType } =
				data as IProcessesTableData;
			return [
				{
					icon: 'risk',
					label: getChipLabel('Risks', riskCount, true),
					count: riskCount,
					className,
					showCount: true,
				},
				{
					icon: 'control',
					label: getChipLabel('Controls', controlCount, true),
					count: controlCount,
					className,
					showCount: true,
				},
				{
					icon: 'industryType',
					label: industryType,
					className,
					showCount: false,
				},
			];
		}
		case CONTROLS_VALUE: {
			const {
				frequency,
				keyControl,
				controlType,
				automatedOrManual,
				industryType,
			} = data as IControlTableData;
			return [
				{
					icon: 'frequency',
					label: frequency,
					className,
					showCount: false,
				},
				{
					icon: 'keyControl',
					label: keyControl,
					className,
					showCount: false,
				},
				{
					icon: 'controlType',
					label: controlType,
					className,
					showCount: false,
				},
				{
					icon: 'automatedOrManual',
					label: automatedOrManual,
					className,
					showCount: false,
				},
				{
					icon: 'industryType',
					label: industryType,
					className,
					showCount: false,
				},
			];
		}
		case RISKS_VALUE: {
			const { processCount, controlCount, industryType } =
				data as IRiskTableProps;
			return [
				{
					icon: 'process',
					label: getChipLabel(
						toSentenceCase(PROCESSES_VALUE),
						processCount,
						true,
					),
					count: processCount,
					showCount: true,
					className,
				},
				{
					icon: 'control',
					label: getChipLabel(
						toSentenceCase(CONTROLS_VALUE),
						controlCount,
						true,
					),
					count: controlCount,
					showCount: true,
					className,
				},
				{ icon: 'industryType', label: industryType, className },
			];
		}
		case OBLIGATIONS_VALUE: {
			const obligationData = data as IObligationTableData;
			const businessUnitCount = {
				icon: 'industryType',
				label: getChipLabel(
					'BusinessUnits',
					getBusinessUnitsCount(obligationData, NAME),
					true,
				),
				className,
			};
			const businessUnitLabel = {
				icon: 'industryType',
				label: getBusinessUnitsObjectStr(obligationData, NAME),
				className,
			};
			return [
				getBusinessUnitsCount(obligationData, NAME) > 1
					? businessUnitCount
					: businessUnitLabel,

				{
					icon: 'process',
					label: getChipLabel(
						toSentenceCase(PROCESSES_VALUE),
						obligationData?.processCount,
						true,
					),
					count: obligationData?.processCount,
					showCount: true,
					className,
				},
				{
					icon: 'risk',
					label: getChipLabel(
						toSentenceCase(RISKS_VALUE),
						obligationData?.riskCount,
						true,
					),
					count: obligationData?.riskCount,
					className,
					showCount: true,
				},
				{
					icon: 'control',
					label: getChipLabel(
						toSentenceCase(CONTROLS_VALUE),
						obligationData?.controlCount,
						true,
					),
					count: obligationData?.controlCount,
					showCount: true,
					className,
				},
				{
					icon: 'policies',
					label: getChipLabel(
						toSentenceCase(POLICIES_VALUE),
						obligationData?.policyCount,
						true,
					),
					count: obligationData?.controlCount,
					showCount: true,
					className,
				},
			];
		}
		case POLICIES_VALUE: {
			const { effectiveFrom, effectiveTo, policyType, version, policyOwner } =
				data as IPoliciesTableData;
			return [
				{
					icon: 'industryType',
					label: policyType,
					className,
				},
				{
					icon: 'number',
					label: version,
					className,
				},
				{
					icon: 'effectivePeriod',
					label: `${dateInCorrectFormatWithFullYear(
						effectiveFrom,
					)} - ${dateInCorrectFormatWithFullYear(effectiveTo)}`,
					className,
				},
				{
					icon: 'owner',
					label: `${OWNER_LABEL}: ${policyOwner}`,
					className,
				},
			];
		}
		default:
			return [];
	}
};

export const getFooterInfo = (
	data: RiskRefCardData,
	category: TAXONOMY_TAB_VALUES,
) => {
	switch (category) {
		case OBLIGATIONS_VALUE: {
			const obligationData = data as IObligationTableData;
			return (
				<Stack direction="row" justifyContent="space-between" mt={2}>
					<Typography
						className={styles['risk-ref-obligations-footer-container']}
					>
						{OWNER_LABEL}&nbsp;:&nbsp;
						{getBusinessUnitsObjectStr(obligationData, USER_NAME)}
					</Typography>
					<Typography
						className={styles['risk-ref-obligations-footer-container']}
					>
						{LAST_UPDATED_LABEL}&nbsp;:&nbsp;
						{dateInCorrectFormatWithFullYear(
							obligationData?.modifiedDate || '',
						)}
					</Typography>
				</Stack>
			);
		}
		case RISKS_VALUE:
			return '';
		default:
			return '';
	}
};
